/* eslint-disable react-hooks/rules-of-hooks */
'use client';

import { createMachine, assign } from 'xstate';
import { ptBrCommon } from '@/lib/pt-br/messages';
import { useMachine } from '@xstate/react';
import SquaredBox from '@/components/ui/squared-box';
import { AddIcon } from '@/components/icons/product/add-icon';
import { Form } from '@/components/ui/forms/form';
import {
  CPFUserInput,
  ISearchProduct,
  PBMSalePrice,
  PBMProgram,
  ICartProduct,
  PBMActivation,
} from '@/types';
import type { SubmitHandler, UseFormReturn } from 'react-hook-form';
import * as yup from 'yup';
import InputButton from '@/components/ui/forms/input-button';
import { cpfMask, removeCpfMask } from '@/lib/utils/mask';
import { cpfValidator } from '@/lib/utils/validation';
import { motion, AnimatePresence } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Header from './pbm-header';
import BoxError from './pbm-error';
import BoxConfirmation from './pbm-confirmation';
import { BoxActivationForm } from './pbm-activation-form';
import { useProgramBenefits } from '@/store/program-benefits-atom';
import { usePBMActivate, usePBMVerify } from '@/framework/pbm';
import PageLoader from '@/components/ui/loaders/page-loader';
import { useCart } from '@/store/quick-cart/cart.context';
import usePrice from '@/lib/use-price';
import { generateCartItem } from '@/store/quick-cart/generate-cart-item';
import { ArrowPrevIcon } from '@/components/icons/arrow-prev';
import { toast } from 'react-toastify';
import BoomerangIcon from '@/assets/icons/boomerang.svg';
import { KitIcon } from '@/components/icons/product/kit-icon';
import { PbmEmbeddedShell } from './pbm-embedded-shell';
import { TextWithAutolinks } from '@/components/ui/text-with-autolinks';
import { getInterplayersInSiteActivationEnabled } from '@/lib/constants/interplayers-in-site-activation';

const cpfFormValidation = yup.object().shape({
  cpf: yup
    .string()
    .required('error-cpf-required')
    .test('cpf', 'Informe um cpf válido', (value = '') => cpfValidator(value)),
});

function SalePriceRow({
  item,
  onSelect,
}: Readonly<{
  item: PBMSalePrice;
  onSelect: (item: Readonly<PBMSalePrice>) => void;
}>) {
  const { price } = usePrice({ amount: item.price });

  return (
    <div className="w-full flex items-center justify-between">
      <span className="text-gray-200 text-lg">
        {item.quantity} Unid. cada por:
      </span>
      <button
        type="button"
        onClick={() => {
          onSelect(item);
        }}
        className="flex items-center gap-2 bg-[#E8F2FB] rounded-[30px] py-2 px-4"
      >
        <AddIcon />
        <span className="text-accent font-bold text-base">
          {price}
        </span>
      </button>
    </div>
  );
}

function DiscountItemSelection({
  salePrices,
  comboSalePrices = [],
  onSelect,
  availableStock,
}: Readonly<{
  salePrices: PBMSalePrice[];
  comboSalePrices?: PBMSalePrice[];
  onSelect: (item: PBMSalePrice) => void;
  availableStock: number;
}>) {
  const validSalePrices = salePrices.filter(
    (item) => item.quantity <= availableStock,
  );
  const validComboSalePrices = comboSalePrices.filter(
    (item) => item.quantity <= availableStock,
  );

  if (validSalePrices.length === 0 && validComboSalePrices.length === 0) {
    return (
      <div className="w-full flex items-center justify-center py-8">
        <span className="text-gray-400 text-lg">
          Nenhuma promoção disponível com estoque suficiente.
        </span>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-4">
      {validSalePrices.map((item, index) => (
        <SalePriceRow
          key={`sale-${index}-${item.quantity}-${item.price}`}
          item={item}
          onSelect={onSelect}
        />
      ))}
      {validComboSalePrices.length > 0 && (
        <>
          <div className="mt-1 flex items-center gap-2.5 border-t border-border-200/60 pt-4">
            <KitIcon className="h-6 w-7 shrink-0" aria-hidden />
            <span className="text-sm font-semibold text-accent md:text-base">
              Descontos de combo
            </span>
          </div>
          {validComboSalePrices.map((item, index) => (
            <SalePriceRow
              key={`combo-${index}-${item.quantity}-${item.price}`}
              item={item}
              onSelect={onSelect}
            />
          ))}
        </>
      )}
    </div>
  );
}

const CPF_INPUT_ID = 'pbm-cpf-input';

function CPFForm({
  cpf,
  onSubmit,
  isLoading,
  serverError,
  renderAfterInput,
}: Readonly<{
  cpf?: string;
  onSubmit: SubmitHandler<CPFUserInput>;
  isLoading?: boolean;
  serverError?: any;
  renderAfterInput?: (methods: UseFormReturn<CPFUserInput>) => React.ReactNode;
}>) {
  const handleChange = (event: any) => {
    const maskedValue = cpfMask(event.target.value);
    event.target.value = maskedValue;
  };

  return (
    <Form
      onSubmit={onSubmit}
      useFormProps={{ defaultValues: { cpf } }}
      validationSchema={cpfFormValidation}
      serverError={serverError && ptBrCommon(serverError)}
      className="text-left w-full"
    >
      {(methods) => (
        <div className="flex flex-col items-center gap-4 w-full">
          <InputButton
            id={CPF_INPUT_ID}
            className="w-full"
            type="text"
            placeholder="Informe seu CPF"
            variant="complementary"
            {...methods.register('cpf', {
              onChange: handleChange,
            })}
            error={ptBrCommon(methods.formState.errors.cpf?.message!)}
            disabledButton={
              !methods.formState.isDirty ||
              !methods.formState.isValid ||
              methods.formState.isSubmitting
            }
          />
          {renderAfterInput?.(methods)}
        </div>
      )}
    </Form>
  );
}

function PbmBenefitButton({
  price,
  showBoomerang,
  priceShownAbove,
  label = 'Ativar benefício',
  methods,
  onValidSubmit,
}: Readonly<{
  price?: string;
  showBoomerang?: boolean;
  priceShownAbove?: boolean;
  label?: string;
  methods: UseFormReturn<CPFUserInput>;
  onValidSubmit: SubmitHandler<CPFUserInput>;
}>) {
  const showPriceInButton = !priceShownAbove && (price || showBoomerang);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const boomerangContent = (() => {
    if (!showBoomerang) return null;
    if (!mounted) {
      return (
        <>
          {[1, 2, 3].map((i) => (
            <span
              key={i}
              className="shrink-0 w-3 h-4 inline-block"
              aria-hidden
            />
          ))}
        </>
      );
    }
    const iconSrc =
      typeof BoomerangIcon === 'string'
        ? BoomerangIcon
        : (BoomerangIcon as { src?: string })?.src;
    if (iconSrc) {
      return (
        <>
          {[1, 2, 3].map((i) => (
            // eslint-disable-next-line @next/next/no-img-element -- rendered only after mount to avoid hydration mismatch
            <img
              key={i}
              src={iconSrc}
              alt=""
              width={12}
              height={16}
              className="shrink-0"
            />
          ))}
        </>
      );
    }
    if (typeof BoomerangIcon === 'function') {
      const Icon = BoomerangIcon as React.FC<{ width?: number; height?: number; className?: string }>;
      return (
        <>
          {[1, 2, 3].map((i) => (
            <Icon key={i} width={12} height={16} className="shrink-0" />
          ))}
        </>
      );
    }
    return null;
  })();

  const isSubmitting = methods.formState.isSubmitting;

  return (
    <button
      type="button"
      disabled={isSubmitting}
      onClick={() => {
        void methods.handleSubmit(onValidSubmit, async () => {
          document.getElementById(CPF_INPUT_ID)?.focus();
          await methods.trigger('cpf');
        })();
      }}
      className="w-full flex rounded-full overflow-hidden border border-[#223F99]/20 cursor-pointer text-left disabled:cursor-not-allowed disabled:opacity-60"
    >
      {showPriceInButton ? (
        <>
          <div className="bg-[#223F99] px-4 py-2.5 flex items-center justify-center gap-1 shrink-0 rounded-l-full rounded-r-xl">
            {showBoomerang ? boomerangContent : (
              price && (
                <span className="text-white font-bold text-sm">{price}</span>
              )
            )}
          </div>
          <div
            className="flex-1 flex items-center justify-center px-4 py-2.5"
            style={{ backgroundColor: 'rgba(34, 63, 153, 0.1)' }}
          >
            <span className="text-[#223F99] text-sm font-bold">
              {label}
            </span>
          </div>
        </>
      ) : (
        <div className="w-full h-12 flex items-center justify-center px-4 bg-[#223F99] rounded-full min-h-[3rem]">
          <span className="text-white text-sm font-bold">
            {label}
          </span>
        </div>
      )}
    </button>
  );
}

function BoxCPF({
  handleSubmit,
  pbmPrice,
  showPbmBoomerang,
  pbmPriceValue,
  hideProminentPrice,
  laboratoryDiscountFootnote,
}: Readonly<{
  handleSubmit: (cpf: string) => void;
  pbmPrice?: string;
  showPbmBoomerang?: boolean;
  pbmPriceValue?: number;
  /** When true, CPF box does not repeat the large price (e.g. shown beside purchase option row). */
  hideProminentPrice?: boolean;
  /** Informative copy from characteristics (e.g. id 38); URLs become external links. */
  laboratoryDiscountFootnote?: string | null;
}>) {
  const showPbmButton = pbmPrice || showPbmBoomerang;
  const showProminentPrice =
    pbmPrice != null && pbmPriceValue != null && pbmPriceValue > 0 && !showPbmBoomerang;
  const showLargePrice = showProminentPrice && !hideProminentPrice;

  const formBlock = (
    <div
      className="flex w-full flex-col items-stretch gap-4"
      {...(showProminentPrice &&
        !hideProminentPrice && {
        itemScope: true,
        itemType: 'https://schema.org/Offer',
        itemProp: 'offers',
      })}
    >
      {showLargePrice && (
        <>
          <meta itemProp="priceCurrency" content="BRL" />
          <span
            className="w-full text-center text-3xl font-bold text-[#223F99] md:text-4xl"
            itemProp="price"
            content={String(pbmPriceValue)}
          >
            {pbmPrice}
          </span>
        </>
      )}
      <CPFForm
        onSubmit={({ cpf }) => handleSubmit(cpf)}
        renderAfterInput={
          showPbmButton || laboratoryDiscountFootnote
            ? (methods) => (
              <div className="flex w-full flex-col gap-3">
                {showPbmButton ? (
                  <PbmBenefitButton
                    price={pbmPrice}
                    showBoomerang={showPbmBoomerang}
                    priceShownAbove={showProminentPrice}
                    label="Comprar com desconto"
                    methods={methods}
                    onValidSubmit={({ cpf }) => handleSubmit(cpf)}
                  />
                ) : null}
                <TextWithAutolinks
                  text={`*O valor final pode variar de acordo com o CPF informado.${laboratoryDiscountFootnote ? ' ' + laboratoryDiscountFootnote : ''}`}
                  className="text-center text-xs leading-relaxed text-body md:text-sm"
                />
              </div>
            )
            : undefined
        }
      />
    </div>
  );

  if (hideProminentPrice) {
    return (
      <PbmEmbeddedShell>
        {formBlock}
      </PbmEmbeddedShell>
    );
  }

  return (
    <SquaredBox>
      <Header />
      <SquaredBox.Content className="justify-center">{formBlock}</SquaredBox.Content>
    </SquaredBox>
  );
}

function BoxProductSelection({
  salePrices,
  comboSalePrices,
  onSelect,
  onBack,
  availableStock,
  embedded,
}: Readonly<{
  salePrices: PBMSalePrice[];
  comboSalePrices?: PBMSalePrice[];
  onSelect: (item: PBMSalePrice) => void;
  onBack: () => void;
  availableStock: number;
  embedded?: boolean;
}>) {
  const body = (
    <>
      <p className="mb-3 text-xs font-medium text-body/90 md:text-sm">
        Escolha a oferta aplicável ao seu CPF.
      </p>
      <div className="flex-grow">
        <DiscountItemSelection
          salePrices={salePrices}
          comboSalePrices={comboSalePrices}
          onSelect={onSelect}
          availableStock={availableStock}
        />
      </div>
      <button
        type="button"
        onClick={onBack}
        className="mt-4 flex items-center gap-1 self-start text-sm font-medium text-accent"
      >
        <ArrowPrevIcon className="h-5 w-5" />
        Voltar
      </button>
    </>
  );

  if (embedded) {
    return <PbmEmbeddedShell>{body}</PbmEmbeddedShell>;
  }

  return (
    <SquaredBox>
      <Header />
      <SquaredBox.Content className="flex flex-grow flex-col p-6">{body}</SquaredBox.Content>
    </SquaredBox>
  );
}

function BoxProgramRegister({
  program,
  onBack,
  embedded,
}: Readonly<{
  program: PBMProgram | null;
  onBack: () => void;
  embedded?: boolean;
}>) {
  const body = (
    <>
      <div className="flex flex-grow flex-col items-center justify-center text-center">
        <p className="mb-3 text-lg font-medium">
          CPF não cadastrado ou não habilitado no produto!
        </p>
        <p className="mb-6 text-sm text-body md:text-base">
          Conclua seu cadastro para habilitar os benefícios deste laboratório.
        </p>
        <a
          href={program?.register_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex cursor-pointer items-center justify-center rounded-full bg-accent px-6 py-2 font-medium text-white transition-colors hover:bg-accent/90"
        >
          Cadastrar CPF
        </a>
      </div>
      <button
        type="button"
        onClick={onBack}
        className="mt-4 flex items-center gap-1 self-start text-sm font-medium text-accent"
      >
        <ArrowPrevIcon className="h-5 w-5" />
        Voltar
      </button>
    </>
  );

  if (embedded) {
    return <PbmEmbeddedShell>{body}</PbmEmbeddedShell>;
  }

  return (
    <SquaredBox>
      <Header />
      <SquaredBox.Content className="flex flex-grow flex-col p-6">{body}</SquaredBox.Content>
    </SquaredBox>
  );
}

const productPBMMachine = createMachine({
  id: 'productPBM',
  initial: 'cpf',
  context: {
    userData: null,
    salePrices: [] as PBMSalePrice[],
    comboSalePrices: [] as PBMSalePrice[],
    program: null as PBMProgram | null,
    selectedPromotion: null as PBMSalePrice | null,
    pbmErrorMessage: null as string | null,
    activation: null as PBMActivation | null,
    activationAttempt: 1,
    activationError: null as string | null,
  },
  states: {
    cpf: {
      on: {
        SUBMIT: 'loading',
      },
    },
    loading: {
      on: {
        NEXT_SUCCESS: {
          target: 'selection',
          actions: assign((_ctx: any) => {
            const { event = {} } = _ctx;

            return {
              userData: event.payload,
              salePrices: event.payload.sale_prices,
              comboSalePrices: event.payload.combo_sale_prices ?? [],
              program: event.payload.program,
              activation: null,
              activationError: null,
            };
          }),
        },
        NEXT_ERROR: {
          target: 'error',
          actions: assign((_ctx: any) => {
            const { event = {} } = _ctx;
            return {
              pbmErrorMessage:
                typeof event.message === 'string' ? event.message : null,
            };
          }),
        },
        NEEDS_REGISTRATION: {
          target: 'program_register',
          actions: assign((_ctx: any) => {
            const { event = {} } = _ctx;
            return {
              userData: event.payload,
              program: event.payload.program,
            };
          }),
        },
        NEEDS_ACTIVATION: {
          target: 'activation_form',
          actions: assign((_ctx: any) => {
            const { event = {} } = _ctx;
            return {
              userData: event.payload,
              program: event.payload.program,
              activation: event.payload.activation,
              activationAttempt: event.payload.attempt ?? 1,
              activationError: null,
            };
          }),
        },
      },
    },
    selection: {
      on: {
        NEXT: {
          target: 'confirmation',
          actions: assign({
            selectedPromotion: (_ctx) => {
              const { event } = _ctx;
              return event.promotion;
            },
          }),
        },
        BACK: 'cpf',
      },
    },
    confirmation: {
      on: { BACK: 'selection' },
    },
    error: {
      on: {
        RETRY: {
          target: 'cpf',
          actions: assign(() => ({ pbmErrorMessage: null })),
        },
        BACK: {
          target: 'cpf',
          actions: assign(() => ({ pbmErrorMessage: null })),
        },
      },
    },
    program_register: {
      on: { BACK: 'cpf' },
    },
    activation_form: {
      on: {
        BACK: {
          target: 'cpf',
          actions: assign(() => ({
            activation: null,
            activationError: null,
            activationAttempt: 1,
          })),
        },
        ACTIVATION_SUBMIT: 'activation_loading',
        ACTIVATION_REFRESH: {
          actions: assign((_ctx: any) => {
            const { event = {}, context = {} } = _ctx;
            return {
              activation: event.payload.activation,
              activationAttempt: event.payload.attempt,
              activationError: event.payload.error ?? null,
              program: event.payload.program ?? context.program,
            };
          }),
        },
      },
    },
    activation_loading: {
      on: {
        BACK: {
          target: 'cpf',
          actions: assign(() => ({
            activation: null,
            activationError: null,
            activationAttempt: 1,
          })),
        },
        NEXT_SUCCESS: {
          target: 'selection',
          actions: assign((_ctx: any) => {
            const { event = {} } = _ctx;
            return {
              userData: event.payload,
              salePrices: event.payload.sale_prices,
              comboSalePrices: event.payload.combo_sale_prices ?? [],
              program: event.payload.program,
              activation: null,
              activationError: null,
            };
          }),
        },
        ACTIVATION_RETRY: {
          target: 'activation_form',
          actions: assign((_ctx: any) => {
            const { event = {}, context = {} } = _ctx;
            return {
              activation: event.payload.activation ?? context.activation,
              activationAttempt:
                event.payload.attempt ?? context.activationAttempt,
              activationError: event.payload.error ?? null,
              program: event.payload.program ?? context.program,
            };
          }),
        },
        NEXT_ERROR: {
          target: 'error',
          actions: assign((_ctx: any) => {
            const { event = {} } = _ctx;
            return {
              pbmErrorMessage:
                typeof event.message === 'string' ? event.message : null,
            };
          }),
        },
      },
    },
  },
  // Global transitions
  on: {
    RESET: {
      target: '.cpf',
      actions: assign(() => ({
        userData: null,
        salePrices: [],
        comboSalePrices: [],
        program: null,
        selectedPromotion: null,
        pbmErrorMessage: null,
        activation: null,
        activationAttempt: 1,
        activationError: null,
      })),
    },
  },
});

function RenderFormSteps({
  product,
  hideProminentPrice,
  onEmbeddedStepChange,
  laboratoryDiscountFootnote,
}: Readonly<{
  product: ISearchProduct;
  hideProminentPrice?: boolean;
  /** Reports PBM state machine step when embedded in purchase options (e.g. hide header prices during selection). */
  onEmbeddedStepChange?: (step: string) => void;
  laboratoryDiscountFootnote?: string | null;
}>) {
  const {
    items: rawItems,
    clearItemFromCart,
    addItemToCart,
    getItemFromCart,
    isInCart,
    updateCartLanguage,
    language,
  } = useCart();

  const originalPriceObj = product.product_prices?.find(
    (p) => p.price_channel_id === 1,
  );
  const pbmPriceObj = product.product_prices?.find(
    (p) => p.price_channel_id === 15,
  );

  const originalPrice =
    originalPriceObj?.sale_price ?? originalPriceObj?.price ?? 0;
  const pbmPriceVal = pbmPriceObj?.sale_price ?? pbmPriceObj?.price ?? 0;

  const showPbmHeaderPrice = pbmPriceVal > 0 && originalPrice >= pbmPriceVal;
  const showPbmBoomerang = pbmPriceVal > 0 && pbmPriceVal > originalPrice;
  /** PBM price is currently offered for this product (e.g. from API). When false after refresh, we show CPF again. */
  const pbmAvailableOnProduct = pbmPriceVal > 0;

  const { price: formattedPbmPrice } = usePrice({ amount: pbmPriceVal });

  const items = rawItems as ICartProduct[];
  const cartPBMItem = useMemo(
    () =>
      items.find(
        (ci: ICartProduct) => ci.sku === product.sku && ci.pbm_info?.flag,
      ) as ICartProduct | undefined,
    [items, product.sku],
  );
  const isPBMInCart = !!cartPBMItem;
  const [state, send] = useMachine(productPBMMachine);
  const { setBenefit, clearBenefit, programBenefits } = useProgramBenefits();
  const [cpf, setCpf] = React.useState<string>('');
  const pbmVerifyMutation = usePBMVerify();
  const pbmActivateMutation = usePBMActivate();
  const inSiteActivationEnabled = getInterplayersInSiteActivationEnabled();
  const hasBenefit = useMemo(
    () => !!programBenefits[product.id],
    [programBenefits, product.id],
  );

  // If in cart with PBM, ensure state machine has the necessary context for navigation
  React.useEffect(() => {
    if (cartPBMItem && state.matches('cpf')) {

      // When item is in cart, we need to set up the state machine context
      // so that going back from confirmation can go to selection
      // We'll transition to selection state (which requires sale prices)
      // But since we're showing confirmation directly, we'll keep state as is
      // and handle the back navigation separately
    } else if (cartPBMItem && state.matches('selection')) {
      // If already in selection, we can transition to confirmation
      const promotion = (cartPBMItem.pbm_info as any)?.promotion;
      if (promotion) {
        send({
          type: 'NEXT',
          promotion,
        });
      }
    }
  }, [cartPBMItem, state, send]);

  const handleSubmit = useCallback(
    async (cpfValue: string) => {
      setCpf(cpfValue);
      send({ type: 'SUBMIT' });

      try {
        const cpfWithoutMask = removeCpfMask(cpfValue);

        const response = await pbmVerifyMutation.mutateAsync({
          sku: product.sku,
          document: cpfWithoutMask,
        } as any);

        if (response.success) {
          send({
            type: 'NEXT_SUCCESS',
            payload: {
              cpf: cpfValue,
              sku: product.sku,
              sale_prices: response.sale_prices ?? [],
              combo_sale_prices: response.combo_sale_prices ?? [],
              program: response.program,
            },
          });
        } else if (response.code === 'F803') {
          send({
            type: 'NEXT_ERROR',
            message: response.message,
          });
        } else if (
          inSiteActivationEnabled &&
          response.needs_activation &&
          response.activation?.fields
        ) {
          send({
            type: 'NEEDS_ACTIVATION',
            payload: {
              cpf: cpfValue,
              sku: product.sku,
              program: response.program,
              activation: response.activation,
              attempt: 1,
            },
          });
        } else {
          send({
            type: 'NEEDS_REGISTRATION',
            payload: {
              cpf: cpfValue,
              sku: product.sku,
              program: response.program,
            },
          });
        }
      } catch (error) {
        send({ type: 'NEXT_ERROR' });
      }
    },
    [send, product.sku, pbmVerifyMutation, inSiteActivationEnabled],
  );

  const handleActivationSubmit = useCallback(
    async (userPayload: Record<string, unknown>) => {
      const ctx = state.context as any;
      const activation = ctx.activation as PBMActivation | null;
      if (!activation) {
        return;
      }

      send({ type: 'ACTIVATION_SUBMIT' });

      try {
        const cpfWithoutMask = removeCpfMask(cpf);
        const attempt = Number(ctx.activationAttempt || 1);
        const response = await pbmActivateMutation.mutateAsync({
          sku: product.sku,
          document: cpfWithoutMask,
          user_payload: userPayload,
          activation_fields: activation.fields,
          session: activation.session || {},
          attempt,
          url_accept_term: activation.url_accept_term,
          url_text_accept_term: activation.url_text_accept_term,
        });

        if (response.success) {
          send({
            type: 'NEXT_SUCCESS',
            payload: {
              cpf,
              sku: product.sku,
              sale_prices: response.sale_prices ?? [],
              combo_sale_prices: response.combo_sale_prices ?? [],
              program: response.program,
            },
          });
          return;
        }

        if (response.code === 'F803') {
          send({
            type: 'NEXT_ERROR',
            message: response.message,
          });
          return;
        }

        if (response.needs_activation && response.activation?.fields) {
          send({
            type: 'ACTIVATION_RETRY',
            payload: {
              activation: response.activation,
              attempt: Math.min(attempt + 1, 3),
              error: response.message,
              program: response.program,
            },
          });
          return;
        }

        send({
          type: 'ACTIVATION_RETRY',
          payload: {
            activation,
            attempt,
            error:
              response.message ||
              'Não foi possível concluir a adesão. Revise os dados e tente novamente.',
            program: response.program ?? ctx.program,
          },
        });
      } catch (error: any) {
        const message =
          error?.response?.data?.message ||
          'Erro ao concluir adesão ao programa laboratorial';
        send({
          type: 'ACTIVATION_RETRY',
          payload: {
            activation,
            attempt: ctx.activationAttempt || 1,
            error: message,
          },
        });
      }
    },
    [state.context, cpf, product.sku, pbmActivateMutation, send],
  );

  const handleProductSelect = useCallback(
    (item: PBMSalePrice) => {
      const ctx = state.context as any;
      const program = ctx?.userData?.program || cartPBMItem?.pbm_info?.program;

      // Set the benefit first
      setBenefit(product.id, {
        price: item.price,
        flag: true,
        salePriceId: item.id,
        cpf,
        quantity: item.quantity,
        program,
        promotion: item,
      });

      // Generate cart item with PBM info
      const cartItem = generateCartItem(
        {
          ...product,
          pbm_flag: true,
          pbm_sale_price_id: item.id,
          pbm_cpf: cpf,
          pbm_quantity: item.quantity,
          pbm_program: program,
          pbm_promotion: item,
          sale_price: item.price,
        },
        undefined,
      );

      // Check if item is already in cart (PBM or non-PBM) by ID first
      let existingCartItem = getItemFromCart(cartItem.id);

      // Also check by SKU in case there are variations with different IDs
      if (!existingCartItem) {
        existingCartItem = items.find(
          (ci: ICartProduct) => ci.sku === product.sku,
        ) as ICartProduct | undefined;
      }

      if (existingCartItem) {
        // If it's already a PBM item with the same promotion, show error
        if (existingCartItem.pbm_info?.flag) {
          toast.error('Este item já está no carrinho com benefício PBM.');
          return;
        }
        // If it's a non-PBM item, remove it first before adding the PBM version
        clearItemFromCart(existingCartItem.id);
      }

      // Check stock availability
      const availableStock = product?.quantity || 0;
      if (item.quantity > availableStock) {
        toast.error('Quantidade em estoque insuficiente para o benefício PBM.');
        return;
      }

      // Update cart language if needed
      if (cartItem?.language && cartItem?.language !== language) {
        updateCartLanguage(cartItem.language);
      }

      // Add to cart with the promotion quantity (exact quantity from the offer)
      addItemToCart(cartItem, item.quantity);

      // Show success message
      toast.success('Produto adicionado ao carrinho com benefício PBM!');

      // Transition to confirmation state
      send({ type: 'NEXT', promotion: item });
    },
    [
      send,
      setBenefit,
      product,
      cpf,
      state.context,
      cartPBMItem,
      getItemFromCart,
      clearItemFromCart,
      addItemToCart,
      updateCartLanguage,
      language,
      items,
    ],
  );

  // Clear benefit if user goes back to CPF or error state, but only when PBM not already in cart
  const prevStateRef = useRef<string | object>(state.value);
  const clearedForStateRef = useRef<string | null>(null);

  // Reset cleared ref when PBM is added to cart
  useEffect(() => {
    if (isPBMInCart) {
      clearedForStateRef.current = null;
    }
  }, [isPBMInCart]);

  useEffect(() => {
    const currentState = state.value;
    const prevState = prevStateRef.current;
    const stateKey = typeof currentState === 'string' ? currentState : JSON.stringify(currentState);

    // Only clear if:
    // 1. PBM is not in cart
    // 2. We're in cpf or error state
    // 3. Benefit actually exists
    // 4. State actually changed (transitioned to cpf/error from another state)
    // 5. We haven't already cleared for this state
    const isInCpfOrError = state.matches('cpf') || state.matches('error');
    const stateChanged = prevState !== currentState;
    const shouldClear =
      !isPBMInCart &&
      isInCpfOrError &&
      hasBenefit &&
      stateChanged &&
      clearedForStateRef.current !== stateKey;

    if (shouldClear) {
      clearBenefit(product.id);
      clearedForStateRef.current = stateKey;
    }

    prevStateRef.current = currentState;
  }, [state, clearBenefit, product.id, isPBMInCart, hasBenefit]);

  // Reset PBM flow when the user picks another variant in the same group.
  const prevProductSkuRef = React.useRef(product.sku);
  useEffect(() => {
    if (prevProductSkuRef.current === product.sku) {
      return;
    }
    prevProductSkuRef.current = product.sku;
    setCpf('');
    clearBenefit(product.id);
    send({ type: 'RESET' });
  }, [product.sku, product.id, clearBenefit, send]);

  // When PBM benefit was in cart and now removed, reset machine
  const prevInCartRef = React.useRef(isPBMInCart);
  useEffect(() => {
    if (prevInCartRef.current && !isPBMInCart) {
      send({ type: 'RESET' });
    }
    prevInCartRef.current = isPBMInCart;
  }, [isPBMInCart, send]);

  // Back to CPF scenarios (without removing cart item or interrupting UX):
  // 1) Item in cart but product no longer has PBM price (e.g. after refresh) → show CPF; cart keeps PBM price.
  // 2) Product lost PBM while in selection/confirmation (not from cart) → reset to CPF.
  useEffect(() => {
    if (isPBMInCart && !pbmAvailableOnProduct) {
      send({ type: 'RESET' });
    }
  }, [isPBMInCart, pbmAvailableOnProduct, send]);

  useEffect(() => {
    if (
      !pbmAvailableOnProduct &&
      !isPBMInCart &&
      (state.matches('selection') || state.matches('confirmation'))
    ) {
      send({ type: 'RESET' });
    }
  }, [pbmAvailableOnProduct, isPBMInCart, state, send]);

  const transitionProps = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 },
    transition: { duration: 0.3 },
  };

  // Handle back from confirmation - removes PBM from cart and clears benefit
  const handleBackFromConfirmation = useCallback(async () => {
    // Clear the benefit first
    clearBenefit(product.id);

    // Remove from cart if it exists
    const wasInCart = !!cartPBMItem;
    const cartCpf = cartPBMItem?.pbm_info?.cpf;
    if (cartPBMItem) {
      clearItemFromCart(cartPBMItem.id);
    }

    // If item was in cart, go back to selection (not reset)
    if (wasInCart) {
      // Item was in cart - we want to go to selection
      // If we're already in confirmation state, just use BACK
      if (state.matches('confirmation')) {
        send({ type: 'BACK' }); // confirmation -> selection
      } else {
        // Not in confirmation state - need to set context and go to selection
        // If we have sale prices in context, use them
        const hasStoredOffers =
          (state.context.salePrices?.length ?? 0) > 0 ||
          (state.context.comboSalePrices?.length ?? 0) > 0;
        if (hasStoredOffers) {
          // Use NEXT_SUCCESS to set the context and transition to selection
          send({
            type: 'NEXT_SUCCESS',
            payload: {
              cpf: cartCpf || cpf,
              sku: product.sku,
              sale_prices: state.context.salePrices,
              combo_sale_prices: state.context.comboSalePrices,
              program: state.context.program || cartPBMItem?.pbm_info?.program,
            },
          });
        } else {
          // No sale prices in context - re-fetch them using CPF from cart item
          if (cartCpf) {
            try {
              const cpfWithoutMask = removeCpfMask(cartCpf);
              const response = await pbmVerifyMutation.mutateAsync({
                sku: product.sku,
                document: cpfWithoutMask,
              } as any);

              if (response.success) {
                // Set the sale prices in context and go to selection
                send({
                  type: 'NEXT_SUCCESS',
                  payload: {
                    cpf: cartCpf,
                    sku: product.sku,
                    sale_prices: response.sale_prices ?? [],
                    combo_sale_prices: response.combo_sale_prices ?? [],
                    program: response.program,
                  },
                });
              }
            } catch (error) {
              // If re-fetch fails, silently fail - user can re-enter CPF
              console.error('Failed to re-fetch sale prices:', error);
            }
          }
        }
      }
    } else {
      // Normal flow - go back to selection if we have sale prices
      const hasStoredOffers =
        (state.context.salePrices?.length ?? 0) > 0 ||
        (state.context.comboSalePrices?.length ?? 0) > 0;
      if (hasStoredOffers && state.matches('confirmation')) {
        send({ type: 'BACK' }); // confirmation -> selection
      }
    }
  }, [
    clearBenefit,
    product.id,
    product.sku,
    cartPBMItem,
    clearItemFromCart,
    state.context.salePrices,
    state.context.comboSalePrices,
    state.context.program,
    state,
    send,
    cpf,
    pbmVerifyMutation,
  ]);

  // Show confirmation only when item is in cart AND product still has PBM price.
  // When product no longer has PBM (e.g. after refresh), we show CPF so state is consistent.
  const showConfirmationFromCart =
    isPBMInCart && pbmAvailableOnProduct;

  const embedded = Boolean(hideProminentPrice);

  useEffect(() => {
    if (!embedded || !onEmbeddedStepChange) return;
    if (showConfirmationFromCart) {
      onEmbeddedStepChange('confirmation');
      return;
    }
    const v = state.value;
    const step = typeof v === 'string' ? v : 'cpf';
    onEmbeddedStepChange(step);
  }, [
    embedded,
    onEmbeddedStepChange,
    showConfirmationFromCart,
    state.value,
  ]);

  return (
    <div className="w-full">
      {showConfirmationFromCart ? (
        <BoxConfirmation
          promotion={
            (cartPBMItem?.pbm_info as any)?.promotion ||
            state.context.selectedPromotion ||
            null
          }
          onBack={handleBackFromConfirmation}
          embedded={embedded}
        />
      ) : (
        <AnimatePresence mode="wait">
          {state.matches('cpf') && (
            <motion.div key="cpf" {...transitionProps}>
              <BoxCPF
                handleSubmit={handleSubmit}
                pbmPrice={showPbmHeaderPrice ? formattedPbmPrice : undefined}
                showPbmBoomerang={showPbmBoomerang}
                pbmPriceValue={pbmPriceVal > 0 ? pbmPriceVal : undefined}
                hideProminentPrice={hideProminentPrice}
                laboratoryDiscountFootnote={laboratoryDiscountFootnote}
              />
            </motion.div>
          )}
          {state.matches('loading') && (
            <motion.div key="loading" {...transitionProps}>
              {embedded ? (
                <PbmEmbeddedShell className="flex min-h-[200px] items-center justify-center py-8">
                  <PageLoader width={200} />
                </PbmEmbeddedShell>
              ) : (
                <SquaredBox>
                  <Header />
                  <SquaredBox.Content className="items-center justify-center">
                    <PageLoader width={200} />
                  </SquaredBox.Content>
                </SquaredBox>
              )}
            </motion.div>
          )}
          {state.matches('selection') && (
            <motion.div key="selection" {...transitionProps}>
              <BoxProductSelection
                salePrices={state.context.salePrices}
                comboSalePrices={state.context.comboSalePrices}
                onSelect={handleProductSelect}
                onBack={() => send({ type: 'BACK' })}
                availableStock={product?.quantity || 0}
                embedded={embedded}
              />
            </motion.div>
          )}
          {state.matches('confirmation') && (
            <motion.div key="confirmation" {...transitionProps}>
              <BoxConfirmation
                promotion={state.context.selectedPromotion}
                onBack={handleBackFromConfirmation}
                embedded={embedded}
              />
            </motion.div>
          )}
          {state.matches('error') && (
            <motion.div key="error" {...transitionProps}>
              <BoxError
                onBack={() => send({ type: 'BACK' })}
                embedded={embedded}
                message={
                  (state.context as { pbmErrorMessage: string | null })
                    .pbmErrorMessage ?? undefined
                }
              />
            </motion.div>
          )}
          {state.matches('program_register') && (
            <motion.div key="program_register" {...transitionProps}>
              <BoxProgramRegister
                program={state.context.program}
                onBack={() => send({ type: 'BACK' })}
                embedded={embedded}
              />
            </motion.div>
          )}
          {(state.matches('activation_form') ||
            state.matches('activation_loading')) &&
            (state.context as any).activation && (
              <motion.div key="activation_form" {...transitionProps}>
                <BoxActivationForm
                  activation={(state.context as any).activation}
                  attempt={(state.context as any).activationAttempt || 1}
                  serverError={(state.context as any).activationError}
                  isSubmitting={
                    state.matches('activation_loading') ||
                    pbmActivateMutation.isLoading
                  }
                  programName={state.context.program?.name}
                  productEan={
                    (state.context as any).userData?.product?.ean ||
                    (product as { ean?: string }).ean ||
                    product.characteristics?.find(
                      (c) =>
                        c.id === 4 ||
                        c.name?.toLowerCase().includes('ean'),
                    )?.value
                  }
                  documentCpf={cpf}
                  onBack={() => send({ type: 'BACK' })}
                  onSubmit={handleActivationSubmit}
                  embedded={embedded}
                />
              </motion.div>
            )}
        </AnimatePresence>
      )}
    </div>
  );
}

export default function ProductPBM({
  product,
  hideProminentPrice,
  onEmbeddedStepChange,
  laboratoryDiscountFootnote,
}: Readonly<{
  product: ISearchProduct;
  hideProminentPrice?: boolean;
  onEmbeddedStepChange?: (step: string) => void;
  laboratoryDiscountFootnote?: string | null;
}>) {
  return (
    <RenderFormSteps
      product={product}
      hideProminentPrice={hideProminentPrice}
      onEmbeddedStepChange={onEmbeddedStepChange}
      laboratoryDiscountFootnote={laboratoryDiscountFootnote}
    />
  );
}
